import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Gauge, Plus, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ENGINEERING_STANDARD_STATUSES,
  ENGINEERING_STANDARD_STATUS_LABELS,
  PERMISSIONS,
  STANDARD_SOURCES,
  STANDARD_SOURCE_LABELS,
  type EngineeringStandardStatus,
  type StandardSource,
} from '@odr/shared';

import { PageHeader } from '../../components/AppShell';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardHeader,
  DetailRow,
  EmptyState,
  Field,
  Input,
  ListRow,
  Modal,
  Select,
  SkeletonRows,
  Spinner,
  Stat,
  StatusPill,
  Textarea,
} from '../../components/ui';
import { ApiError, api, qs } from '../../lib/api';
import { useAuth, useCan } from '../../lib/auth';
import { formatDate, formatDateTime, humanise } from '../../lib/format';

interface StandardRow {
  id: string;
  code: string;
  revisionNo: number;
  parameter: string;
  status: string;
  targetValue: number | null;
  minValue: number | null;
  maxValue: number | null;
  unit: string | null;
  referenceDocument: string | null;
  referenceNumber: string | null;
  revision: string | null;
  machineCode: string | null;
  componentName: string | null;
  usedByCount: number;
  /** A revision in progress, which the standard's own status does not reveal. */
  pendingRevisionNo: number | null;
  pendingRevisionStatus: string | null;
}

const STATUS_TONE: Record<string, 'ok' | 'info' | 'warn' | 'neutral'> = {
  DRAFT: 'warn',
  ENGINEERING_REVIEW: 'info',
  APPROVED: 'info',
  ACTIVE: 'ok',
  SUPERSEDED: 'neutral',
  WITHDRAWN: 'neutral',
};

export function StandardsPage() {
  const [statusFilter, setStatusFilter] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const canManage = useCan(PERMISSIONS.STANDARD_MANAGE);

  const { data, isLoading } = useQuery({
    queryKey: ['standards', statusFilter],
    queryFn: () =>
      api.get<{ data: StandardRow[]; total: number }>(
        `/api/engineering-standards${qs({ status: statusFilter, pageSize: 200 })}`,
      ),
  });

  const { data: coverage } = useQuery({
    queryKey: ['standards', 'coverage'],
    queryFn: () =>
      api.get<{
        scored: number;
        recordOnly: number;
        awaitingEngineering: number;
        standardsAwaitingEngineering: number;
      }>('/api/engineering-standards/coverage'),
  });

  return (
    <div>
      <PageHeader
        title="Engineering standards"
        subtitle="Controlled acceptance limits. A reading is only scored once a tolerance is approved and in service."
        actions={
          canManage ? (
            <Button icon={Plus} onClick={() => setCreateOpen(true)}>
              New standard
            </Button>
          ) : null
        }
      />

      <div className="space-y-4 p-4 lg:p-6">
        <Callout tone="info" title="Why some readings are not scored">
          Where a source document gives a target but no acceptance tolerance — “Torque 25 Nm” —
          ODR records the reading and does not judge it. Inventing a tolerance would be inventing
          an engineering standard. An engineer sets the limits here, cites the document they came
          from, and puts them into service; only then does that reading start producing pass, fail
          and Problem Tags.
        </Callout>

        {coverage ? (
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Stat label="Scored checks" value={coverage.scored} tone="ok" icon={CheckCircle2} />
            <Stat
              label="Record only"
              value={coverage.recordOnly}
              tone="neutral"
              hint="Not a failure — an honest state"
              icon={Gauge}
            />
            <Stat
              label="Awaiting engineering"
              value={coverage.awaitingEngineering}
              tone={coverage.awaitingEngineering > 0 ? 'warn' : 'ok'}
              hint="Checks with a target but no tolerance"
            />
            <Stat
              label="Standards in progress"
              value={coverage.standardsAwaitingEngineering}
              tone={coverage.standardsAwaitingEngineering > 0 ? 'warn' : 'ok'}
              icon={ShieldCheck}
            />
          </div>
        ) : null}

        <Card className="p-3">
          <Field label="Status">
            <Select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
              <option value="">All standards</option>
              {ENGINEERING_STANDARD_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {ENGINEERING_STANDARD_STATUS_LABELS[status]}
                </option>
              ))}
            </Select>
          </Field>
        </Card>

        <Card>
          {isLoading ? (
            <SkeletonRows rows={4} />
          ) : !data?.data.length ? (
            <EmptyState
              icon={Gauge}
              title="No standards yet"
              message="Create one for each measured check that needs a controlled acceptance range."
            />
          ) : (
            <ul className="divide-y divide-steel-100">
              {data.data.map((standard) => (
                <li key={standard.id}>
                  <ListRow onClick={() => setOpenId(standard.id)}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-steel-900">
                          <span className="font-mono text-info-700">{standard.code}</span>
                          <span className="ml-1.5 text-xs font-semibold text-steel-500">
                            Rev {standard.revisionNo}
                          </span>{' '}
                          {standard.parameter}
                        </p>
                        <p className="mt-0.5 text-xs text-steel-500">
                          {standard.machineCode ? `${standard.machineCode} · ` : ''}
                          {standard.componentName ?? 'Machine level'}
                          {standard.usedByCount > 0
                            ? ` · used by ${standard.usedByCount} task${standard.usedByCount === 1 ? '' : 's'}`
                            : ' · not linked to any task'}
                        </p>
                        <p className="mt-1 text-xs font-semibold text-steel-700">
                          {standard.minValue != null || standard.maxValue != null ? (
                            <>
                              Acceptance: {standard.minValue ?? '−∞'} to {standard.maxValue ?? '∞'}
                              {standard.unit ? ` ${standard.unit}` : ''}
                            </>
                          ) : (
                            <span className="text-warn-700">
                              Target {standard.targetValue ?? '—'}
                              {standard.unit ? ` ${standard.unit}` : ''} · no acceptance tolerance
                            </span>
                          )}
                        </p>
                        {standard.referenceNumber || standard.referenceDocument ? (
                          <p className="text-xs text-steel-400">
                            {[standard.referenceDocument, standard.referenceNumber, standard.revision]
                              .filter(Boolean)
                              .join(' · ')}
                          </p>
                        ) : null}
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-1">
                        <StatusPill
                          tone={STATUS_TONE[standard.status] ?? 'neutral'}
                          label={ENGINEERING_STANDARD_STATUS_LABELS[
                            standard.status as EngineeringStandardStatus
                          ] ?? humanise(standard.status)}
                        />
                        {/*
                          A revision waiting on an approver is work nobody can
                          see from the standard's own status, which stays ACTIVE.
                        */}
                        {standard.pendingRevisionStatus &&
                        standard.pendingRevisionStatus !== 'DRAFT' ? (
                          <Badge
                            tone={
                              standard.pendingRevisionStatus === 'PENDING_APPROVAL' ? 'warn' : 'info'
                            }
                          >
                            Rev {standard.pendingRevisionNo}{' '}
                            {standard.pendingRevisionStatus === 'PENDING_APPROVAL'
                              ? 'awaiting approval'
                              : 'approved, not in service'}
                          </Badge>
                        ) : standard.pendingRevisionStatus === 'DRAFT' &&
                          standard.status === 'ACTIVE' ? (
                          <Badge tone="neutral">Rev {standard.pendingRevisionNo} draft</Badge>
                        ) : null}
                      </div>
                    </div>
                  </ListRow>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {createOpen ? <CreateStandardModal onClose={() => setCreateOpen(false)} /> : null}
      {openId ? <StandardDetail id={openId} onClose={() => setOpenId(null)} /> : null}
    </div>
  );
}

/** Route target for /engineering-standards/:id — opens the detail directly. */
export function StandardDetailPage() {
  const { id = '' } = useParams();
  return <StandardsPage key={id} />;
}

type RevisionApprovalStatus =
  | 'DRAFT'
  | 'PENDING_APPROVAL'
  | 'APPROVED'
  | 'ACTIVE'
  | 'SUPERSEDED'
  | 'REJECTED';

interface RevisionRecord {
  id: string;
  revisionNo: number;
  approvalStatus: RevisionApprovalStatus;
  targetValue: number | null;
  minValue: number | null;
  maxValue: number | null;
  unit: string | null;
  referenceDocument: string | null;
  referenceNumber: string | null;
  revisionLabel: string | null;
  changeReason: string | null;
  rejectionReason: string | null;
  preparedBy: string;
  preparedByName: string | null;
  preparedAt: string;
  approvedBy: string | null;
  approvedByName: string | null;
  approvedAt: string | null;
  activatedByName: string | null;
  activatedAt: string | null;
  isCurrent: boolean;
}

const REVISION_TONE: Record<RevisionApprovalStatus, 'neutral' | 'info' | 'ok' | 'warn' | 'alarm'> = {
  DRAFT: 'neutral',
  PENDING_APPROVAL: 'warn',
  APPROVED: 'info',
  ACTIVE: 'ok',
  SUPERSEDED: 'neutral',
  REJECTED: 'alarm',
};

const REVISION_LABEL: Record<RevisionApprovalStatus, string> = {
  DRAFT: 'Draft',
  PENDING_APPROVAL: 'Awaiting approval',
  APPROVED: 'Approved — not yet in service',
  ACTIVE: 'In service',
  SUPERSEDED: 'Superseded',
  REJECTED: 'Rejected',
};

interface StandardDetailData {
  standard: {
    id: string;
    code: string;
    revisionNo: number;
    parameter: string;
    description: string | null;
    status: EngineeringStandardStatus;
    targetValue: number | null;
    minValue: number | null;
    maxValue: number | null;
    unit: string | null;
    source: string;
    referenceDocument: string | null;
    referenceNumber: string | null;
    revision: string | null;
    approvedAt: string | null;
    activatedAt: string | null;
    effectiveDate: string | null;
    reviewDate: string | null;
  };
  machine: { code: string; name: string } | null;
  component: { name: string } | null;
  preparedByName: string | null;
  approvedByName: string | null;
  activatedByName: string | null;
  /**
   * The controlled revisions. Dual control applies to each of these separately:
   * whoever last shaped a revision's numbers may not be the one to approve it,
   * no matter who authored the standard (Phase 11.2).
   */
  revisionRecords: RevisionRecord[];
  openRevision: RevisionRecord | null;
  currentRevision: { revisionNo: number } | null;
  revisions: Array<{
    id: string;
    revisionNo: number;
    changeType: string;
    fromStatus: string | null;
    toStatus: string | null;
    beforeJson: (Record<string, unknown> & { revisionNo?: number }) | null;
    afterJson: (Record<string, unknown> & { revisionNo?: number }) | null;
    reason: string;
    changedByName: string;
    changedAt: string;
  }>;
  usedBy: Array<{ itemId: string; seq: number; task: string }>;
  affectedTemplates: Array<{
    template_code: string;
    template_name: string;
    version_no: number;
    version_status: string;
    seq: number;
    task: string;
    machines: string | null;
  }>;
  allowedTransitions: EngineeringStandardStatus[];
  hasLimits: boolean;
  isScoring: boolean;
}

function StandardDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [reviseOpen, setReviseOpen] = useState(false);
  const [transitionTo, setTransitionTo] = useState<EngineeringStandardStatus | null>(null);

  const canManage = useCan(PERMISSIONS.STANDARD_MANAGE);
  const canApprove = useCan(PERMISSIONS.STANDARD_APPROVE);
  const currentUserId = useAuth((state) => state.user?.id ?? null);

  const { data, isLoading } = useQuery({
    queryKey: ['standard', id],
    queryFn: () => api.get<StandardDetailData>(`/api/engineering-standards/${id}`),
  });

  const transition = useMutation({
    mutationFn: (input: { to: EngineeringStandardStatus; reason: string }) =>
      api.post(`/api/engineering-standards/${id}/transition`, input),
    onSuccess: () => {
      setError(null);
      setTransitionTo(null);
      void queryClient.invalidateQueries();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'That change could not be made.'),
  });

  if (isLoading || !data) {
    return (
      <Modal open onClose={onClose} title="Engineering standard">
        <Spinner label="Loading standard" />
      </Modal>
    );
  }

  const { standard, openRevision } = data;
  // The numbers live on the open revision, so editability follows it.
  const editable =
    openRevision?.approvalStatus === 'DRAFT' ||
    openRevision?.approvalStatus === 'PENDING_APPROVAL';
  const inService = standard.status === 'ACTIVE';
  /**
   * Dual control, surfaced before the click rather than as a 422 afterwards.
   * The server enforces it regardless — this only explains it.
   */
  const preparedByMe = openRevision != null && openRevision.preparedBy === currentUserId;

  return (
    <>
      <Modal
        open
        onClose={onClose}
        title={`${standard.code} Rev ${standard.revisionNo} — ${standard.parameter}`}
        footer={
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" fullWidth onClick={onClose}>
              Close
            </Button>
            {canManage && editable ? (
              <Button variant="secondary" fullWidth onClick={() => setEditOpen(true)}>
                Change limits
              </Button>
            ) : null}
            {/*
              Preparing a revision is gated on `manage`, not `approve`:
              proposing a change is not the controlled act. The draft changes
              nothing until a second person approves it.
            */}
            {canManage && inService && !openRevision ? (
              <Button variant="secondary" fullWidth onClick={() => setReviseOpen(true)}>
                Prepare a revision
              </Button>
            ) : null}
            {data.allowedTransitions.map((to) => {
              const needsEngineering = to === 'APPROVED' || to === 'ACTIVE';
              if (needsEngineering && !canApprove) return null;
              if (!needsEngineering && !canManage) return null;
              // Approving or rejecting your own figures is refused by the
              // server; don't offer the button that is going to bounce.
              const blockedBySelf = preparedByMe && (to === 'APPROVED' || to === 'DRAFT');
              return (
                <Button
                  key={to}
                  fullWidth
                  disabled={blockedBySelf}
                  variant={to === 'ACTIVE' ? 'success' : 'primary'}
                  onClick={() => setTransitionTo(to)}
                >
                  {to === 'ENGINEERING_REVIEW'
                    ? 'Submit for review'
                    : to === 'APPROVED'
                      ? blockedBySelf
                        ? 'Approve — needs someone else'
                        : `Approve Rev ${openRevision?.revisionNo ?? standard.revisionNo}`
                      : to === 'ACTIVE'
                        ? `Put Rev ${openRevision?.revisionNo ?? standard.revisionNo} into service`
                        : to === 'DRAFT'
                          ? 'Send back for changes'
                          : humanise(to)}
                </Button>
              );
            })}
          </div>
        }
      >
        <div className="space-y-4">
          {error ? <Callout tone="alarm">{error}</Callout> : null}

          {openRevision && inService ? (
            <Callout
              tone={openRevision.approvalStatus === 'APPROVED' ? 'info' : 'warn'}
              title={`Rev ${openRevision.revisionNo} is ${REVISION_LABEL[openRevision.approvalStatus].toLowerCase()}`}
            >
              Readings are still judged against Rev {data.currentRevision?.revisionNo ?? standard.revisionNo}{' '}
              ({standard.minValue ?? '−∞'} to {standard.maxValue ?? '∞'}
              {standard.unit ? ` ${standard.unit}` : ''}). Rev {openRevision.revisionNo} proposes{' '}
              {openRevision.minValue ?? '−∞'} to {openRevision.maxValue ?? '∞'}
              {openRevision.unit ? ` ${openRevision.unit}` : ''} and changes nothing until it is
              approved by someone other than {openRevision.preparedByName ?? 'its preparer'} and put
              into service.
            </Callout>
          ) : null}

          {preparedByMe && openRevision?.approvalStatus === 'PENDING_APPROVAL' ? (
            <Callout tone="info" title="You prepared this revision">
              Someone else has to approve it. That is the point of the control — the person who
              set the limits a machine is judged against is not the person who signs them off.
            </Callout>
          ) : null}

          {data.isScoring ? (
            <Callout tone="ok" title="In service — readings are scored">
              Checks linked to this standard are judged against {standard.minValue} to{' '}
              {standard.maxValue} {standard.unit ?? ''} and can raise Problem Tags.
            </Callout>
          ) : !data.hasLimits ? (
            <Callout tone="warn" title="No acceptance tolerance">
              This standard records a target but no acceptance range, so linked checks are
              recorded and not scored. Set a minimum and maximum from a reference document, then
              have Engineering approve it.
            </Callout>
          ) : (
            <Callout tone="info" title="Limits set, not yet in service">
              Linked checks are still recorded without being scored until this is approved and put
              into service.
            </Callout>
          )}

          <dl className="rounded-lg border border-steel-200 px-3 py-1">
            <DetailRow label="Status">
              <StatusPill
                tone={STATUS_TONE[standard.status] ?? 'neutral'}
                label={ENGINEERING_STANDARD_STATUS_LABELS[standard.status]}
              />
            </DetailRow>
            <DetailRow label="Parameter">{standard.parameter}</DetailRow>
            <DetailRow label="Applies to">
              {data.machine ? `${data.machine.code} — ${data.machine.name}` : 'Any machine'}
              {data.component ? ` · ${data.component.name}` : ''}
            </DetailRow>
            <DetailRow label="Target">
              {standard.targetValue ?? '—'}
              {standard.unit ? ` ${standard.unit}` : ''}
            </DetailRow>
            <DetailRow label="Acceptance limits">
              {standard.minValue != null || standard.maxValue != null ? (
                <span className="font-semibold">
                  {standard.minValue ?? '−∞'} to {standard.maxValue ?? '∞'}
                  {standard.unit ? ` ${standard.unit}` : ''}
                </span>
              ) : (
                <span className="font-semibold text-warn-700">Not configured</span>
              )}
            </DetailRow>
            <DetailRow label="Source">
              {STANDARD_SOURCE_LABELS[standard.source as StandardSource] ?? standard.source}
            </DetailRow>
            <DetailRow label="Reference">
              {[standard.referenceDocument, standard.referenceNumber, standard.revision]
                .filter(Boolean)
                .join(' · ') || 'Not cited'}
            </DetailRow>
            <DetailRow label="Prepared by">{data.preparedByName}</DetailRow>
            <DetailRow label="Approved by">
              {data.approvedByName
                ? `${data.approvedByName} · ${formatDateTime(standard.approvedAt)}`
                : 'Not approved'}
            </DetailRow>
            <DetailRow label="Put into service by">
              {data.activatedByName
                ? `${data.activatedByName} · ${formatDateTime(standard.activatedAt)}`
                : 'Not in service'}
            </DetailRow>
            <DetailRow label="Effective">{formatDate(standard.effectiveDate)}</DetailRow>
            <DetailRow label="Next review">{formatDate(standard.reviewDate)}</DetailRow>
          </dl>

          {data.revisionRecords.length > 0 ? (
            <Card>
              <CardHeader
                title="Controlled revisions"
                subtitle="Each revision is prepared by one person and approved by another. Runs keep the revision they were performed against."
              />
              <ol className="px-4 py-2">
                {data.revisionRecords.map((revision) => (
                  <li
                    key={revision.id}
                    className="border-b border-steel-100 py-3 last:border-0"
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <p className="text-sm font-bold text-steel-900">
                        Rev {revision.revisionNo}{' '}
                        <Badge tone={REVISION_TONE[revision.approvalStatus]}>
                          {REVISION_LABEL[revision.approvalStatus]}
                        </Badge>
                      </p>
                      <p className="font-mono text-xs text-steel-600">
                        {revision.minValue == null && revision.maxValue == null
                          ? `target ${revision.targetValue ?? '—'}${revision.unit ? ` ${revision.unit}` : ''} · no tolerance`
                          : `${revision.minValue ?? '−∞'} to ${revision.maxValue ?? '∞'}${revision.unit ? ` ${revision.unit}` : ''}`}
                      </p>
                    </div>
                    {revision.changeReason ? (
                      <p className="mt-1 text-sm text-steel-700">{revision.changeReason}</p>
                    ) : null}
                    <dl className="mt-1 grid grid-cols-1 gap-x-4 text-xs text-steel-600 sm:grid-cols-2">
                      <div>
                        <span className="text-steel-500">Prepared by </span>
                        <span className="font-semibold">
                          {revision.preparedByName ?? 'Unknown'}
                        </span>
                        <span className="text-steel-400"> · {formatDateTime(revision.preparedAt)}</span>
                      </div>
                      <div>
                        <span className="text-steel-500">Approved by </span>
                        {revision.approvedByName ? (
                          <>
                            <span className="font-semibold">{revision.approvedByName}</span>
                            <span className="text-steel-400">
                              {' '}
                              · {formatDateTime(revision.approvedAt)}
                            </span>
                          </>
                        ) : (
                          <span className="font-semibold text-warn-700">Not yet approved</span>
                        )}
                      </div>
                      {revision.activatedByName ? (
                        <div>
                          <span className="text-steel-500">Into service by </span>
                          <span className="font-semibold">{revision.activatedByName}</span>
                          <span className="text-steel-400">
                            {' '}
                            · {formatDateTime(revision.activatedAt)}
                          </span>
                        </div>
                      ) : null}
                      {[revision.referenceDocument, revision.referenceNumber, revision.revisionLabel]
                        .filter(Boolean)
                        .length > 0 ? (
                        <div>
                          <span className="text-steel-500">Reference </span>
                          <span className="font-semibold">
                            {[
                              revision.referenceDocument,
                              revision.referenceNumber,
                              revision.revisionLabel,
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </span>
                        </div>
                      ) : null}
                    </dl>
                    {revision.rejectionReason ? (
                      <p className="mt-1 text-xs text-alarm-700">
                        Sent back: {revision.rejectionReason}
                      </p>
                    ) : null}
                  </li>
                ))}
              </ol>
            </Card>
          ) : null}

          {data.affectedTemplates.length > 0 ? (
            <Card>
              <CardHeader
                title="Affected checklist templates"
                subtitle="Revising this standard changes how future runs of these are judged. Nothing is relinked automatically — review them."
              />
              <ul className="px-4 py-2">
                {data.affectedTemplates.map((row, index) => (
                  <li
                    key={`${row.template_code}-${row.seq}-${index}`}
                    className="border-b border-steel-100 py-2 text-sm last:border-0"
                  >
                    <p className="font-semibold text-steel-900">
                      {row.template_code} v{row.version_no}{' '}
                      <Badge tone={row.version_status === 'PUBLISHED' ? 'ok' : 'warn'}>
                        {humanise(row.version_status)}
                      </Badge>
                    </p>
                    <p className="text-xs text-steel-600">
                      <span className="font-semibold">#{row.seq}</span> {row.task}
                    </p>
                    {row.machines ? (
                      <p className="text-xs text-steel-500">Scheduled on {row.machines}</p>
                    ) : (
                      <p className="text-xs text-steel-400">Not scheduled on any machine</p>
                    )}
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}

          <RunsUsingStandard standardId={id} />

          {data.usedBy.length > 0 ? (
            <Card>
              <CardHeader
                title="Checklist tasks using this"
                subtitle="Changing this standard changes how these are judged"
              />
              <ul className="px-4 py-2">
                {data.usedBy.map((item) => (
                  <li key={item.itemId} className="border-b border-steel-100 py-2 text-sm last:border-0">
                    <span className="font-semibold text-steel-500">#{item.seq}</span> {item.task}
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}

          <Card>
            <CardHeader
              title="Change history"
              subtitle="Every limit change and approval, with the previous value"
            />
            <ol className="px-4 py-2">
              {data.revisions.map((revision) => (
                <li key={revision.id} className="border-b border-steel-100 py-3 last:border-0">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    {/*
                      "Change N" not "Rev N": these are change-log entries, and
                      the standard's own revision number is a different thing.
                      Two meanings of "Rev" in one panel misleads an auditor.
                    */}
                    <p className="text-sm font-bold text-steel-900">
                      Change {revision.revisionNo} · {humanise(revision.changeType)}
                      {revision.toStatus && revision.toStatus !== revision.fromStatus
                        ? ` → ${humanise(revision.toStatus)}`
                        : ''}
                    </p>
                    <p className="text-xs text-steel-500">
                      {revision.changedByName} · {formatDateTime(revision.changedAt)}
                    </p>
                  </div>
                  <p className="mt-1 text-sm text-steel-700">{revision.reason}</p>
                  {revision.changeType === 'LIMITS_CHANGED' || revision.changeType === 'REVISED' ? (
                    <p className="mt-1 font-mono text-xs text-steel-500">
                      {formatLimits(revision.beforeJson)} → {formatLimits(revision.afterJson)}
                      {revision.changeType === 'REVISED' &&
                      revision.beforeJson?.revisionNo != null &&
                      revision.afterJson?.revisionNo != null
                        ? `  (standard Rev ${String(revision.beforeJson.revisionNo)} → Rev ${String(revision.afterJson.revisionNo)})`
                        : ''}
                    </p>
                  ) : null}
                </li>
              ))}
            </ol>
          </Card>
        </div>
      </Modal>

      {editOpen ? (
        <EditLimitsModal
          id={id}
          initial={standard}
          revision={openRevision}
          onClose={() => setEditOpen(false)}
          onSaved={() => {
            setEditOpen(false);
            void queryClient.invalidateQueries();
          }}
        />
      ) : null}

      {reviseOpen ? (
        <ReviseModal
          id={id}
          initial={standard}
          affected={data.affectedTemplates}
          onClose={() => setReviseOpen(false)}
          onSaved={() => {
            setReviseOpen(false);
            void queryClient.invalidateQueries();
          }}
        />
      ) : null}

      {transitionTo ? (
        <TransitionModal
          to={transitionTo}
          standard={standard}
          revision={openRevision}
          busy={transition.isPending}
          onClose={() => setTransitionTo(null)}
          onSubmit={(reason) => transition.mutate({ to: transitionTo, reason })}
        />
      ) : null}
    </>
  );
}

function formatLimits(snapshot: Record<string, unknown> | null): string {
  if (!snapshot) return '—';
  const min = snapshot.minValue ?? '−∞';
  const max = snapshot.maxValue ?? '∞';
  const unit = snapshot.unit ? ` ${snapshot.unit}` : '';
  if (snapshot.minValue == null && snapshot.maxValue == null) return `no limits${unit}`;
  return `${min} to ${max}${unit}`;
}

function EditLimitsModal({
  id,
  initial,
  revision,
  onClose,
  onSaved,
}: {
  id: string;
  initial: StandardDetailData['standard'];
  /** The open revision being edited — its figures, not the ones in service. */
  revision: RevisionRecord | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    minValue: (revision?.minValue ?? initial.minValue)?.toString() ?? '',
    maxValue: (revision?.maxValue ?? initial.maxValue)?.toString() ?? '',
    targetValue: (revision?.targetValue ?? initial.targetValue)?.toString() ?? '',
    unit: revision?.unit ?? initial.unit ?? '',
    source: initial.source,
    referenceDocument: revision?.referenceDocument ?? initial.referenceDocument ?? '',
    referenceNumber: revision?.referenceNumber ?? initial.referenceNumber ?? '',
    revision: revision?.revisionLabel ?? initial.revision ?? '',
    reason: '',
  });
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/api/engineering-standards/${id}`, {
        minValue: numberOrNull(form.minValue),
        maxValue: numberOrNull(form.maxValue),
        targetValue: numberOrNull(form.targetValue),
        unit: form.unit || null,
        source: form.source,
        referenceDocument: form.referenceDocument || null,
        referenceNumber: form.referenceNumber || null,
        revision: form.revision || null,
        reason: form.reason,
      }),
    onSuccess: onSaved,
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not save the limits.'),
  });

  const set = (key: keyof typeof form) => (value: string) =>
    setForm((current) => ({ ...current, [key]: value }));

  return (
    <Modal
      open
      onClose={onClose}
      title={
        revision ? `Set limits on Rev ${revision.revisionNo}` : 'Set acceptance limits'
      }
      footer={
        <div className="flex gap-2">
          <Button variant="secondary" fullWidth onClick={onClose}>
            Cancel
          </Button>
          <Button
            fullWidth
            loading={save.isPending}
            disabled={form.reason.trim().length < 3}
            onClick={() => save.mutate()}
          >
            Save
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error ? <Callout tone="alarm">{error}</Callout> : null}
        <Callout tone="info">
          The previous values are kept in the change history. Nothing is overwritten silently.
        </Callout>
        {/*
          Two things the person editing has to know, both of which the server
          enforces: they become the preparer (so they cannot approve it), and
          an edit pulls the revision back out of review.
        */}
        {revision ? (
          <Callout tone="warn" title={`You become the preparer of Rev ${revision.revisionNo}`}>
            Saving records you as the person who set these figures, so someone else has to approve
            them.
            {revision.approvalStatus === 'PENDING_APPROVAL'
              ? ' Rev ' +
                revision.revisionNo +
                ' also goes back to draft and has to be resubmitted — nobody approves numbers that changed after they read them.'
              : ''}
          </Callout>
        ) : null}

        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Minimum">
            <Input
              inputMode="decimal"
              value={form.minValue}
              onChange={(e) => set('minValue')(e.target.value)}
            />
          </Field>
          <Field label="Target">
            <Input
              inputMode="decimal"
              value={form.targetValue}
              onChange={(e) => set('targetValue')(e.target.value)}
            />
          </Field>
          <Field label="Maximum">
            <Input
              inputMode="decimal"
              value={form.maxValue}
              onChange={(e) => set('maxValue')(e.target.value)}
            />
          </Field>
        </div>

        <Field label="Unit" required>
          <Input value={form.unit} onChange={(e) => set('unit')(e.target.value)} placeholder="Nm" />
        </Field>

        <Field label="Where do these limits come from?" required>
          <Select value={form.source} onChange={(e) => set('source')(e.target.value)}>
            {STANDARD_SOURCES.map((source) => (
              <option key={source} value={source}>
                {STANDARD_SOURCE_LABELS[source]}
              </option>
            ))}
          </Select>
        </Field>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Reference document" required>
            <Input
              value={form.referenceDocument}
              onChange={(e) => set('referenceDocument')(e.target.value)}
              placeholder="Machine manual MX-880"
            />
          </Field>
          <Field label="Section / number">
            <Input
              value={form.referenceNumber}
              onChange={(e) => set('referenceNumber')(e.target.value)}
              placeholder="§4.2"
            />
          </Field>
        </div>

        <Field label="Document revision">
          <Input
            value={form.revision}
            onChange={(e) => set('revision')(e.target.value)}
            placeholder="Rev C"
          />
        </Field>

        <Field label="Reason for this change" required hint="Recorded in the change history.">
          <Textarea
            value={form.reason}
            onChange={(e) => set('reason')(e.target.value)}
            rows={3}
            placeholder="e.g. Tolerance taken from the sealer manual, section 4.2."
          />
        </Field>
      </div>
    </Modal>
  );
}

/**
 * Runs already evaluated against this standard, with the revision each used.
 *
 * The point of showing it here: it answers "which standard and revision decided
 * this result?" from the record itself, not by inferring it from today's
 * configuration.
 */
function RunsUsingStandard({ standardId }: { standardId: string }) {
  const { data } = useQuery({
    queryKey: ['standard', standardId, 'runs'],
    queryFn: () =>
      api.get<{
        data: Array<{
          runId: string;
          revisionNo: number | null;
          resolvedAt: string;
          source: string;
          startedAt: string | null;
          status: string;
          machineCode: string;
          templateCode: string;
        }>;
      }>(`/api/engineering-standards/${standardId}/runs`),
  });

  if (!data?.data.length) return null;

  const byRevision = data.data.reduce<Record<string, number>>((acc, row) => {
    const key = row.revisionNo == null ? 'unknown' : `Rev ${row.revisionNo}`;
    acc[key] = (acc[key] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <Card>
      <CardHeader
        title="Runs judged against this standard"
        subtitle={Object.entries(byRevision)
          .map(([rev, count]) => `${count} × ${rev}`)
          .join(' · ')}
      />
      <ul className="max-h-64 overflow-y-auto px-4 py-2">
        {data.data.slice(0, 30).map((row) => (
          <li
            key={row.runId}
            className="flex items-center justify-between gap-2 border-b border-steel-100 py-2 text-sm last:border-0"
          >
            <span className="min-w-0">
              <Link to={`/checklists/${row.runId}/review`} className="font-semibold hover:underline">
                {row.machineCode} · {row.templateCode}
              </Link>
              <span className="block text-xs text-steel-500">
                {formatDateTime(row.startedAt)} · {humanise(row.status)}
              </span>
            </span>
            <span className="shrink-0 text-right">
              <Badge tone={row.revisionNo == null ? 'neutral' : 'info'}>
                Rev {row.revisionNo ?? '?'}
              </Badge>
              {row.source === 'BACKFILL' ? (
                <span className="mt-0.5 block text-[10px] text-warn-700">reconstructed</span>
              ) : null}
            </span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function ReviseModal({
  id,
  initial,
  affected,
  onClose,
  onSaved,
}: {
  id: string;
  initial: StandardDetailData['standard'];
  affected: StandardDetailData['affectedTemplates'];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    minValue: initial.minValue?.toString() ?? '',
    maxValue: initial.maxValue?.toString() ?? '',
    targetValue: initial.targetValue?.toString() ?? '',
    referenceDocument: initial.referenceDocument ?? '',
    referenceNumber: initial.referenceNumber ?? '',
    revision: initial.revision ?? '',
    reason: '',
  });
  const [error, setError] = useState<string | null>(null);

  const revise = useMutation({
    mutationFn: () =>
      api.post(`/api/engineering-standards/${id}/revise`, {
        minValue: numberOrNull(form.minValue),
        maxValue: numberOrNull(form.maxValue),
        targetValue: numberOrNull(form.targetValue),
        referenceDocument: form.referenceDocument || null,
        referenceNumber: form.referenceNumber || null,
        revision: form.revision || null,
        reason: form.reason,
      }),
    onSuccess: onSaved,
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not issue the revision.'),
  });

  const set = (key: keyof typeof form) => (value: string) =>
    setForm((current) => ({ ...current, [key]: value }));

  return (
    <Modal
      open
      onClose={onClose}
      title={`Prepare ${initial.code} Rev ${initial.revisionNo + 1}`}
      footer={
        <div className="flex gap-2">
          <Button variant="secondary" fullWidth onClick={onClose}>
            Cancel
          </Button>
          <Button
            fullWidth
            loading={revise.isPending}
            disabled={form.reason.trim().length < 3}
            onClick={() => revise.mutate()}
          >
            Prepare revision
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error ? <Callout tone="alarm">{error}</Callout> : null}

        <Callout tone="info" title="This is a proposal — it changes nothing yet">
          Rev {initial.revisionNo + 1} is saved as a draft. Readings stay judged against Rev{' '}
          {initial.revisionNo} until someone other than you approves it and puts it into service.
          Checklist runs already performed keep Rev {initial.revisionNo} and are never re-scored.
        </Callout>

        {affected.length > 0 ? (
          <Callout tone="info" title={`${affected.length} checklist task(s) affected`}>
            <ul className="mt-1 space-y-0.5">
              {affected.slice(0, 6).map((row, index) => (
                <li key={index}>
                  • {row.template_code} #{row.seq}
                  {row.machines ? ` (${row.machines})` : ''}
                </li>
              ))}
            </ul>
            <p className="mt-1 font-semibold">
              Nothing is relinked automatically — review these before it goes into service.
            </p>
          </Callout>
        ) : null}

        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Minimum" required>
            <Input
              inputMode="decimal"
              value={form.minValue}
              onChange={(e) => set('minValue')(e.target.value)}
            />
          </Field>
          <Field label="Target">
            <Input
              inputMode="decimal"
              value={form.targetValue}
              onChange={(e) => set('targetValue')(e.target.value)}
            />
          </Field>
          <Field label="Maximum" required>
            <Input
              inputMode="decimal"
              value={form.maxValue}
              onChange={(e) => set('maxValue')(e.target.value)}
            />
          </Field>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Reference document" required>
            <Input
              value={form.referenceDocument}
              onChange={(e) => set('referenceDocument')(e.target.value)}
            />
          </Field>
          <Field label="Document revision">
            <Input
              value={form.revision}
              onChange={(e) => set('revision')(e.target.value)}
              placeholder="Rev D"
            />
          </Field>
        </div>

        <Field label="Why is this being revised?" required>
          <Textarea
            value={form.reason}
            onChange={(e) => set('reason')(e.target.value)}
            rows={3}
            placeholder="e.g. Manual reissued as Rev D with a tighter band."
          />
        </Field>
      </div>
    </Modal>
  );
}

function TransitionModal({
  to,
  standard,
  revision,
  busy,
  onClose,
  onSubmit,
}: {
  to: EngineeringStandardStatus;
  standard: StandardDetailData['standard'];
  /** The revision being moved, when there is one — its numbers are the ones at stake. */
  revision: RevisionRecord | null;
  busy: boolean;
  onClose: () => void;
  onSubmit: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');
  const activating = to === 'ACTIVE';
  // why read the revision and not the standard: the standard row still holds
  // the limits currently in service. Showing those while asking someone to
  // approve different ones is how a wrong tolerance gets signed off.
  const revNo = revision?.revisionNo ?? standard.revisionNo;
  const min = revision ? revision.minValue : standard.minValue;
  const max = revision ? revision.maxValue : standard.maxValue;
  const unit = (revision ? revision.unit : standard.unit) ?? '';

  return (
    <Modal
      open
      onClose={onClose}
      title={
        to === 'APPROVED'
          ? `Approve ${standard.code} Rev ${revNo}`
          : activating
            ? `Put Rev ${revNo} into service`
            : to === 'DRAFT'
              ? `Send Rev ${revNo} back for changes`
              : `Move to ${humanise(to)}`
      }
      footer={
        <div className="flex gap-2">
          <Button variant="secondary" fullWidth onClick={onClose}>
            Cancel
          </Button>
          <Button
            fullWidth
            variant={activating ? 'success' : 'primary'}
            loading={busy}
            disabled={reason.trim().length < 3}
            onClick={() => onSubmit(reason.trim())}
          >
            Confirm
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {activating ? (
          <Callout tone="warn" title="This enables scoring">
            Every checklist task linked to {standard.code} will start being judged against{' '}
            {min ?? '−∞'} to {max ?? '∞'} {unit}. Readings outside that range will become abnormal
            and can raise Problem Tags — including on checklist versions that are already
            published. Runs already performed keep the revision they were judged against.
          </Callout>
        ) : to === 'APPROVED' ? (
          <Callout tone="info" title={`Approving ${min ?? '−∞'} to ${max ?? '∞'} ${unit}`}>
            {revision?.preparedByName
              ? `Prepared by ${revision.preparedByName}. `
              : ''}
            Approving records you, the time, the limits and the reference against Rev {revNo}. It
            must be someone other than whoever prepared it.
          </Callout>
        ) : to === 'DRAFT' ? (
          <Callout tone="info">
            Rev {revNo} goes back to draft for the preparer to amend. Nothing in service changes.
          </Callout>
        ) : null}

        <Field label="Reason" required hint="Recorded in the change history and the audit log.">
          <Textarea
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            rows={3}
            placeholder="e.g. Verified against MX-880 Rev C section 4.2."
          />
        </Field>
      </div>
    </Modal>
  );
}

function CreateStandardModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    parameter: '',
    description: '',
    targetValue: '',
    unit: '',
    machineId: '',
    componentId: '',
  });
  const [error, setError] = useState<string | null>(null);

  const { data: machines } = useQuery({
    queryKey: ['machines', 'picker'],
    queryFn: () => api.get<{ data: Array<{ id: string; code: string; name: string }> }>('/api/machines'),
  });

  const { data: components } = useQuery({
    queryKey: ['components', form.machineId],
    queryFn: () =>
      api.get<{ data: Array<{ id: string; name: string }> }>(
        `/api/machines/${form.machineId}/components`,
      ),
    enabled: Boolean(form.machineId),
  });

  const create = useMutation({
    mutationFn: () =>
      api.post('/api/engineering-standards', {
        parameter: form.parameter.trim(),
        description: form.description || null,
        targetValue: numberOrNull(form.targetValue),
        unit: form.unit || null,
        machineId: form.machineId || null,
        componentId: form.componentId || null,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['standards'] });
      onClose();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not create the standard.'),
  });

  const set = (key: keyof typeof form) => (value: string) =>
    setForm((current) => ({ ...current, [key]: value }));

  return (
    <Modal
      open
      onClose={onClose}
      title="New engineering standard"
      footer={
        <div className="flex gap-2">
          <Button variant="secondary" fullWidth onClick={onClose}>
            Cancel
          </Button>
          <Button
            fullWidth
            loading={create.isPending}
            disabled={!form.parameter.trim()}
            onClick={() => create.mutate()}
          >
            Create draft
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error ? <Callout tone="alarm">{error}</Callout> : null}
        <Callout tone="info">
          Start with the target from the source document. Acceptance limits are set and approved
          separately — a standard without limits records readings without scoring them.
        </Callout>

        <Field label="Parameter" required>
          <Input
            value={form.parameter}
            onChange={(e) => set('parameter')(e.target.value)}
            placeholder="Sealing jaw bolt torque"
          />
        </Field>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Target value">
            <Input
              inputMode="decimal"
              value={form.targetValue}
              onChange={(e) => set('targetValue')(e.target.value)}
              placeholder="25"
            />
          </Field>
          <Field label="Unit">
            <Input value={form.unit} onChange={(e) => set('unit')(e.target.value)} placeholder="Nm" />
          </Field>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Machine">
            <Select value={form.machineId} onChange={(e) => set('machineId')(e.target.value)}>
              <option value="">Any machine</option>
              {(machines?.data ?? []).map((machine) => (
                <option key={machine.id} value={machine.id}>
                  {machine.code} — {machine.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Component">
            <Select
              value={form.componentId}
              onChange={(e) => set('componentId')(e.target.value)}
              disabled={!form.machineId}
            >
              <option value="">No component</option>
              {(components?.data ?? []).map((component) => (
                <option key={component.id} value={component.id}>
                  {component.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <Field label="Notes" hint="Where the target came from.">
          <Textarea
            value={form.description}
            onChange={(e) => set('description')(e.target.value)}
            rows={3}
          />
        </Field>
      </div>
    </Modal>
  );
}

function numberOrNull(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed.replace(',', '.'));
  return Number.isNaN(parsed) ? null : parsed;
}

export { Badge };
